/** Bridge protocol + shared CLI types. */

/** A forwarded tool request the instance sends down the bridge. */
export interface ToolRequest {
  type: "tool_request";
  /** In-memory await id (non-durable calls reply over the WebSocket with this). */
  id?: string;
  /**
   * Durable calls: the agent parked this call and is awaiting the result over
   * HTTP. Reply by POSTing to /tool-result with `toolCallId` instead of the WS.
   */
  durable?: boolean;
  toolCallId?: string;
  tool: string;
  args: Record<string, unknown>;
  requestPermission: string | null;
  risk: number | null;
}

/** The CLI's reply to a tool request. */
export interface ToolResponse {
  type: "tool_response";
  id: string;
  ok: boolean;
  result?: string;
  error?: string;
}

/** Result of executing a tool on the host. */
export interface HostResult {
  ok: boolean;
  result?: string;
  error?: string;
}

/**
 * Permission is a single auto-accept LEVEL 1–5: every tool call at risk ≤ level
 * runs automatically; anything riskier asks. (The catastrophic-command guard
 * still blocks dangerous commands at every level.) shift-tab cycles the level.
 */
export type Level = 1 | 2 | 3 | 4 | 5;

export const LEVELS: Level[] = [1, 2, 3, 4, 5];

/** What each level lets run without asking (risk meanings: 1 read · 2 safe writes/builds · 3 installs/project edits · 4 outside-project/git-history · 5 destructive). */
const LEVEL_DETAIL: Record<Level, string> = {
  1: "only safe reads run automatically",
  2: "+ safe writes & builds",
  3: "+ installs & project edits",
  4: "+ outside-project & git-history",
  5: "everything — never ask",
};

export function levelLabel(level: Level): string {
  return `auto-accept level ${level} (${LEVEL_DETAIL[level]})`;
}

/** Stored credential for one instance. */
export interface InstanceCredential {
  endpoint: string;
  access_token: string;
  token_type: string;
  user?: { id: string; username: string; role: string };
  saved_at: number;
}

export interface CredentialsFile {
  default_endpoint?: string;
  instances: Record<string, InstanceCredential>;
}

export interface ThreadSummary {
  id: string;
  tags: string[];
  created_at?: number;
  title?: string;
  preview?: string;
}
